import * as React from "react";
import { Grid, Paper, FormControlLabel, Checkbox } from "@mui/material";
import Cookies from "js-cookie";
import Title from "@/components/molecules/Title";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import toast from "@/components/hoc/SnackbarAlert";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { getOfficesAPI } from "@/util/api/API";
import { getOfficeOptionLabel } from "@/util";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { ONLY_CONTRACT } from "@/models/office";
import {
  getAccidentReportPersonsAPI,
  getMlitFlowPersonsAPI,
} from "@/util/api/accidentReportPersons";
import type { Office } from "@/models/office";
import type {
  AccidentReportPerson,
  MlitFlowPerson,
} from "@/models/accidentReportPersons";
import { AccidentReportPersonsConstants as Constants } from "./AccidentReportPersonsConstants";
import { AccidentReportPersonsList } from "./AccidentReportPersonsList";
import { MlitFlowResponsiblesSection } from "@/components/accidentReportPersons/MlitFlowResponsiblesSection";
import { AccidentReportPersonsGuide } from "@/components/accidentReportPersons/AccidentReportPersonsGuide";
import { ResponsibleRegistrationPrompt } from "@/components/accidentReportPersons/ResponsibleRegistrationPrompt";

/** 一覧の検索条件。詳細・登録画面から戻ったときに復元する */
export type AccidentReportPersonsSearchState = {
  officeId: number | null;
  name: string;
  hideRetired: boolean;
};

/** search()の結果として画面に反映する状態。関連する複数の値（一覧・0件判定・Mlit一覧・
 * 検索実行時点の営業所と条件）が常に整合した組み合わせで更新されるよう、個別のuseStateではなく
 * 1回のdispatchでまとめて更新するreducerにしている */
type SearchResultState = {
  loading: boolean;
  persons: AccidentReportPerson[];
  noSearchResult: boolean;
  mlitFlowPersons: MlitFlowPerson[];
  // 検索実行時点の営業所。一覧と国交省フロー組込み責任者セクションはこの営業所で固定する
  searchedOffice: Office | null;
  // 営業所と同じく検索実行時点の値。未検索の入力値で一覧が入れ替わらないようにする
  searchedCondition: { name: string; hideRetired: boolean };
};

type SearchResultAction =
  | { type: "start" }
  | {
      type: "settled";
      office: Office;
      condition: { name: string; hideRetired: boolean };
      persons: AccidentReportPerson[];
      // 取得成功時のみ0件表示の判定を更新する（失敗時は直前の表示を維持する）
      noSearchResult?: boolean;
      mlitFlowPersons: MlitFlowPerson[];
    };

const searchResultReducer = (
  state: SearchResultState,
  action: SearchResultAction,
): SearchResultState => {
  switch (action.type) {
    case "start":
      return { ...state, loading: true };
    case "settled":
      return {
        ...state,
        loading: false,
        persons: action.persons,
        noSearchResult: action.noSearchResult ?? state.noSearchResult,
        mlitFlowPersons: action.mlitFlowPersons,
        searchedOffice: action.office,
        searchedCondition: action.condition,
      };
  }
};

export const AccidentReportPersons = (): JSX.Element => {
  const { navigate, savedState } =
    useStatefulNavigator<AccidentReportPersonsSearchState>();

  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office | null>(
    null,
  );
  const [name, setName] = React.useState(savedState?.name ?? "");
  const [hideRetired, setHideRetired] = React.useState(
    savedState?.hideRetired ?? true,
  );

  const [searchResult, dispatchSearchResult] = React.useReducer(
    searchResultReducer,
    {
      loading: false,
      persons: [],
      noSearchResult: false,
      mlitFlowPersons: [],
      searchedOffice: null,
      searchedCondition: {
        name: savedState?.name ?? "",
        hideRetired: savedState?.hideRetired ?? true,
      },
    },
  );
  const {
    loading,
    persons,
    noSearchResult,
    mlitFlowPersons,
    searchedOffice,
    searchedCondition,
  } = searchResult;

  // search()の多重呼び出しのうち、最後に呼ばれたものだけ結果を反映するためのトークン。
  // 初期表示時の自動検索とユーザー操作による検索が重なりうるため、後から呼ばれた検索に
  // 追い越された古い結果を適用してしまう（stateの巻き戻り・トーストの二重発火）のを防ぐ。
  const searchTokenRef = React.useRef(0);

  // 氏名は暗号化して保持しているためクライアント側では絞り込めない。
  // 部分一致判定はサーバ側で復号・正規化して行うため、検索のたびにAPIを呼ぶ。
  //
  // コーディングルールが求める30件ページング・ソートAPIパラメータには対応していない。
  // 営業所ごとの事故報告責任者は BUSINESS_REGISTRATION_LIMIT_PER_OFFICE でアクティブ数が
  // 上限されており、削除済みを含めても実運用で30件規模になることは想定しにくい。
  // また件数が少ないため、ソート順の違いによるユーザーの探しやすさへの影響も小さいと判断し、
  // どちらも今回は見送っている。
  // さらにこの画面は事故報告責任者・国交省フロー組込み責任者の2つのリストを1画面に表示しており、
  // 両方に個別のページング・ソートUIを持たせると画面が煩雑になる。件数が小さいことと合わせて、
  // シンプルな一覧表示を優先している。
  const search = React.useCallback(
    async (office: Office, searchName: string, activeOnly: boolean) => {
      const token = ++searchTokenRef.current;
      dispatchSearchResult({ type: "start" });
      // 事故報告責任者・国交省フロー組込み責任者は独立したセクションのため、Promise.allではなく
      // allSettledで個別に反映する。片方が失敗しても、もう片方の取得結果まで破棄しないようにする
      const [personsResult, mlitResult] = await Promise.allSettled([
        getAccidentReportPersonsAPI(office.id, searchName, activeOnly),
        getMlitFlowPersonsAPI(office.id),
      ]);

      if (token !== searchTokenRef.current) {
        return;
      }

      let persons: AccidentReportPerson[];
      let noSearchResult: boolean | undefined;
      if (personsResult.status === "fulfilled") {
        persons = personsResult.value.data ?? [];
        noSearchResult = persons.length === 0;
      } else {
        console.error(
          "==Error when fetch accidentReportPersons==",
          personsResult.reason,
        );
        persons = [];
        toast.error(Constants.FetchError);
      }

      let mlitFlowPersons: MlitFlowPerson[];
      if (mlitResult.status === "fulfilled") {
        mlitFlowPersons = mlitResult.value.data ?? [];
      } else {
        console.error(
          "==Error when fetch mlitFlowPersons==",
          mlitResult.reason,
        );
        mlitFlowPersons = [];
        toast.error(Constants.FetchMlitError);
      }

      // 一覧・0件判定・Mlit一覧・検索実行時点の営業所と条件を、整合した組み合わせで
      // まとめて反映する
      dispatchSearchResult({
        type: "settled",
        office,
        condition: { name: searchName, hideRetired: activeOnly },
        persons,
        noSearchResult,
        mlitFlowPersons,
      });
    },
    [],
  );

  React.useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await getOfficesAPI(ONLY_CONTRACT);
        const fetched = response.data ?? [];
        setOffices(fetched);

        // 前回の検索条件があればそれを、なければ前回選択して閉じた営業所（Cookie）を初期選択して検索する。
        // どちらもなければ未選択のままにする
        const initial =
          fetched.find((office) => office.id === savedState?.officeId) ??
          fetched.find(
            (office) => office.id === Number(Cookies.get(SINGLE_OFFICE_ID)),
          );
        if (initial) {
          setOfficeSelected(initial);
          search(
            initial,
            savedState?.name ?? "",
            savedState?.hideRetired ?? true,
          );
        }
      } catch (error) {
        console.error("==Error when fetch offices==", error);
      }
    };
    fetchOffices();
    // 初回のみ実行する（savedState は遷移時に確定しているため依存に含めない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOffice = (office: Office | null) => {
    setOfficeSelected(office);
    Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() ?? "");
  };

  const handleClickSearch = () => {
    if (!officeSelected) {
      toast.error(Constants.OfficeRequiredMessage);
      return;
    }
    search(officeSelected, name, hideRetired);
  };

  const handleClickReset = () => {
    setOfficeSelected(null);
    setName("");
    setHideRetired(true);
  };

  const handleReloadMlit = () => {
    if (searchedOffice) {
      search(
        searchedOffice,
        searchedCondition.name,
        searchedCondition.hideRetired,
      );
    }
  };

  const searchState = (): AccidentReportPersonsSearchState => ({
    officeId: searchedOffice?.id ?? null,
    name: searchedCondition.name,
    hideRetired: searchedCondition.hideRetired,
  });

  // 一覧の行数分クロージャが再生成されるのを防ぐため、テーブルへ渡すハンドラを安定させる
  const handleClickDetail = React.useCallback(
    (id: number) => {
      navigate(`/accident_report_persons/${id}`, {
        officeId: searchedOffice?.id ?? null,
        name: searchedCondition.name,
        hideRetired: searchedCondition.hideRetired,
      });
    },
    [navigate, searchedOffice, searchedCondition],
  );

  const handleClickCreate = () => {
    navigate("/accident_report_persons/create", searchState());
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>{Constants.ListTitle}</Title>
          <CommonButton
            btnType="Singup"
            sx={{ height: 35 }}
            onClickBtn={handleClickCreate}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        {/* 他の一覧画面と同じく、内容は入れ子の Grid container に入れる。
            spacing 由来の負の左マージンで、外側の container の左端に内容の左端をそろえる。 */}
        <Grid container spacing={2}>
          {/* 登録中の責任者が0〜1名の営業所がある場合の登録促進バナー。
              一覧の営業所選択とは無関係に、参照可能な営業所すべてを対象とする。 */}
          <ResponsibleRegistrationPrompt />

          {/* 画面の趣旨と使い方。注意事項もここで先に伝える。
              説明と検索条件の区切りが分かるよう、下に余白を足す。 */}
          <Grid item xs={12} sx={{ mb: 2 }}>
            <AccidentReportPersonsGuide />
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AutoCompleteSearch
                    label={Constants.OfficeName}
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected}
                    onChange={(_, value) => onChangeOffice(value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextFieldForSearch
                    label={Constants.NameSearchLabel}
                    value={name}
                    setValue={setName}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={3} container alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideRetired}
                        onChange={(e) => setHideRetired(e.target.checked)}
                      />
                    }
                    label={Constants.HideRetiredLabel}
                  />
                </Grid>
                <Grid item xs={12} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleClickSearch}
                    disabled={loading}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                    onClickBtn={handleClickReset}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <AccidentReportPersonsList
                persons={persons}
                onClickDetail={handleClickDetail}
              />
              {noSearchResult && <EmptyResultMessage />}
            </Paper>
          </Grid>

          {/* 事故報告責任者一覧とは別テーブル・別枠の簡易セクション。受講状況・在任状態は持たない。
              事業者が行えるのは登録と削除のみで、登録内容の変更はGO社内へ依頼する。 */}
          {searchedOffice && (
            <Grid item xs={12} md={12} lg={12}>
              <MlitFlowResponsiblesSection
                officeId={searchedOffice.id}
                officeName={searchedOffice.displayName}
                persons={mlitFlowPersons}
                onReload={handleReloadMlit}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};
